import { t } from 'i18next';
export type PDFSection =
  | 'coverPage'
  | 'summary'
  | 'summaryInvestor'
  | 'aboutCompany'
  | 'aboutCompanyInvestor'
  | 'productAndServices'
  | 'marketAnalysis'
  | 'marketAnalysisInvestor'
  | 'marketingAndSales'
  | 'marketingAndSalesInvestor'
  | 'financialPlan'
  | 'financialPlanInvestor'
  | 'businessOperations'
  | 'businessOperationsInvestor'
  | 'managementAndOrganization'
  | 'managementAndOrganizationInvestor'
  | 'implementationPlan'
  | 'legalFrameworkAndRisks'
  | 'raisingAndAllocatingFunds'
  | 'raisingAndAllocatingFundsInvestor'
  | 'lastPage';

export type SectionsType = {
  [key in PDFSection]: any;
};

export type SummaryKeys =
  | 'goalsAndObjectives'
  | 'companyDescription'
  | 'productsOrServices'
  | 'marketAndCustomers'
  | 'organisationAndTeam'
  | 'financialInformation'
  | 'financingNeeds'
  | 'riskManagementStrategies';

export const getSummaryChapterNames = (isExamplePage: boolean) =>
  ({
    goalsAndObjectives: isExamplePage ? 'Goals and Objectives' : t('goalsAndObjectives'),
    companyDescription: isExamplePage ? 'Company Description' : t('companyDescription'),
    productsOrServices: isExamplePage ? 'Products or Services' : t('productsOrServices'),
    marketAndCustomers: isExamplePage ? 'Market and Customers' : t('marketAndCustomers'),
    organisationAndTeam: isExamplePage ? 'Organization and Team' : t('organizationAndTeam'),
    financialInformation: isExamplePage ? 'Financial Information' : t('financialInformation'),
    financingNeeds: isExamplePage ? 'Financing Needs' : t('financingNeeds'),
    riskManagementStrategies: isExamplePage ? 'Risks and Risk Management Strategies' : t('risks'),
  }) as const;

export type AboutCompanyKeys =
  | 'generalInformation'
  | 'companyHistory'
  | 'aboutTheProject'
  | 'missionAndStrategy'
  | 'stages'
  | 'swotAnalysis';

export const getAboutCompanyChapterNames = (isExamplePage: boolean) =>
  ({
    generalInformation: isExamplePage ? 'General Company Information' : t('company.general.title'),
    companyHistory: isExamplePage ? 'Company History' : t('company.history.title'),
    aboutTheProject: isExamplePage ? 'About the Project' : t('chapters.ABOUT_THE_PROJECT'),
    missionAndStrategy: isExamplePage ? 'Mission, Vision and Strategy' : t('company.mission.title'),
    stages: isExamplePage ? 'Implementation Plan' : t('implementationPlan.title'),
    swotAnalysis: isExamplePage ? 'SWOT Analysis' : t('company.swot.title'),
  }) as const;

export type ProductsAndServicesKeys = 'description' | 'advantages' | 'rAndD' | 'deliveryAndSupport';

export const getProductAndServicesChapterNames = (isExamplePage: boolean) =>
  ({
    description: isExamplePage ? 'Products and Services' : t('productsAndServicesForm.description.title'),
    advantages: isExamplePage ? 'Uniqueness, Competitive Advantage' : t('productsAndServicesForm.uniqueness.title'),
    rAndD: isExamplePage ? 'R&D' : t('productsAndServicesForm.researchAndDevelopment.title'),
    deliveryAndSupport: isExamplePage ? 'Delivery and Support' : t('productsAndServicesForm.deliveryAndSupport.title'),
  }) as const;

export type MarketAnalysisKeys = 'marketOverview' | 'competitiveAnalysis' | 'consumerNeeds';

export const getMarketingAnalysisChapterNames = (isExamplePage: boolean) =>
  ({
    marketOverview: isExamplePage ? 'Market Overview' : t('marketAnalysisForm.overview.title'),
    competitiveAnalysis: isExamplePage ? 'Competitive Analysis' : t('marketAnalysisForm.competitors.title'),
    consumerNeeds: isExamplePage ? 'Consumer Needs' : t('marketAnalysisForm.consumers.title'),
  }) as const;

export type MarketingAndSalesKeys = 'salesPlan' | 'marketingPlan';

export const getMarketingAndSalesChapterNames = (isExamplePage: boolean) =>
  ({
    salesPlan: isExamplePage ? 'Sales Plan' : t('marketingAndSales.sales.title'),
    marketingPlan: isExamplePage ? 'Marketing Plan' : t('marketingAndSales.marketing.title'),
  }) as const;

export type BusinessOperationsKeys =
  | 'location'
  | 'equipment'
  | 'assets'
  | 'technologicalProcess'
  | 'workingResources'
  | 'partners'
  | 'production'
  | 'quality'
  | 'productDescription'
  | 'advantages';

export const getBusinessOperationsChapterNames = (isExamplePage: boolean) =>
  ({
    location: isExamplePage ? 'Location and Premises' : t('businessOperations.location.title'),
    equipment: isExamplePage ? 'Equipment' : t('businessOperations.equipment.title'),
    assets: isExamplePage ? 'Other Assets' : t('businessOperations.assets.title'),
    technologicalProcess: isExamplePage ? 'Technological Process' : t('businessOperations.technologicalProcess.title'),
    workingResources: isExamplePage ? 'Supply of Working Resources' : t('businessOperations.workingResources.title'),
    partners: isExamplePage ? 'Partners and Suppliers' : t('businessOperations.partners.title'),
    production: isExamplePage ? 'Production' : t('businessOperations.production.title'),
    quality: isExamplePage ? 'Quality Control' : t('businessOperations.quality.title'),
    productDescription: isExamplePage
      ? 'Products/Services Description'
      : t('productsAndServicesForm.description.title'),
    advantages: isExamplePage ? 'Uniqueness, Competitive Advantage' : t('productsAndServicesForm.uniqueness.title'),
  }) as const;

export type ManagementAndOrganizationKeys = 'management' | 'partners' | 'staff';

export const getManagementAndOrganizationChapterNames = (isExamplePage: boolean) =>
  ({
    management: isExamplePage ? 'Management Team' : t('management.team.title'),
    partners: isExamplePage ? 'External Partners and Advisers' : t('management.partners.title'),
    staff: isExamplePage ? 'Staff Organization' : t('management.staff.title'),
  }) as const;

export type ImplementationPlanKeys = 'wholePage';

export type RaisingAndAllocatingFundsKeys = 'wholePage';

export type LegalFrameworkAndRisksKeys =
  | 'generalLaws'
  | 'industryRequirements'
  | 'legalRequirements'
  | 'listOfActions'
  | 'listOfRisks';

export const getLegalFrameworkAndRisksChapterNames = (isExamplePage: boolean) =>
  ({
    generalLaws:isExamplePage ? 'General Laws and Regulations' : t('legalFrameworkAndRisks.pdfChapterNames.generalLaws'),
    industryRequirements:isExamplePage ? 'Specific Industry Requirement' : t('legalFrameworkAndRisks.pdfChapterNames.industryRequirements'),
    legalRequirements:isExamplePage ? 'Compliance with Legal Requirements' : t('legalFrameworkAndRisks.pdfChapterNames.legalRequirements'),
    listOfActions:isExamplePage ? 'Legal Support Actions' : t('legalFrameworkAndRisks.pdfChapterNames.listOfActions'),
    listOfRisks:isExamplePage ? 'Risk Assessment' : t('legalFrameworkAndRisks.pdfChapterNames.listOfRisks'),
  }) as const;

export type FinancialPlanKeys =
  | 'keyAssumption'
  | 'profitLossProjection'
  | 'projectedCashFlow'
  | 'projectedBalanceSheet'
  | 'financialPerformance';

export const getFinancialPlanChapterNames = (isExamplePage: boolean) =>
  ({
    keyAssumption:isExamplePage ? 'Key Assumptions' : t('financialPlan.keyAssumptions.title'),
    profitLossProjection:isExamplePage ? 'Profit and Loss Projection' : t('financialPlan.profitAndLossProjection.title'),
    projectedCashFlow:isExamplePage ? 'Projected Cash Flow' : t('financialPlan.projectedCashFlow.title'),
    projectedBalanceSheet:isExamplePage ? 'Projected Balance Sheets' : t('financialPlan.projectedBalanceSheets.title'),
    financialPerformance:isExamplePage ? 'Financial Performance' : t('financialPlan.financialPerformance.title'),
  }) as const;

export type SummarySectionAvailability = { [key in SummaryKeys]: boolean };
export type AboutCompanySectionAvailability = { [key in AboutCompanyKeys]: boolean };
export type ProductsAndServicesSectionAvailability = { [key in ProductsAndServicesKeys]: boolean };
export type MarketAnalysisSectionAvailability = { [key in MarketAnalysisKeys]: boolean };
export type MarketingAndSalesSectionAvailability = { [key in MarketingAndSalesKeys]: boolean };
export type BusinessOperationsSectionAvailability = { [key in BusinessOperationsKeys]: boolean };
export type ManagementAndOrganizationSectionAvailability = { [key in ManagementAndOrganizationKeys]: boolean };
export type ImplementationPlanSectionAvailability = { [key in ImplementationPlanKeys]: boolean };
export type RaisingAndAllocatingFundsSectionAvailability = { [key in RaisingAndAllocatingFundsKeys]: boolean };
export type LegalFrameworkAndRisksSectionAvailability = { [key in LegalFrameworkAndRisksKeys]: boolean };
export type FinancialPlanSectionAvailability = { [key in FinancialPlanKeys]: boolean };

export type SectionsAvailability = {
  summary: SummarySectionAvailability;
  aboutCompany: AboutCompanySectionAvailability;
  productsAndServices: ProductsAndServicesSectionAvailability;
  marketAnalysis: MarketAnalysisSectionAvailability;
  marketingAndSales: MarketingAndSalesSectionAvailability;
  businessOperations: BusinessOperationsSectionAvailability;
  managementAndOrganization: ManagementAndOrganizationSectionAvailability;
  implementationPlan: ImplementationPlanSectionAvailability;
  raisingAndAllocatingFunds: RaisingAndAllocatingFundsSectionAvailability;
  legalFrameworkAndRisks: LegalFrameworkAndRisksSectionAvailability;
  financialPlan: FinancialPlanSectionAvailability;
};

export type SectionsAvailabilityKeys = keyof SectionsAvailability;
